// src/pages/earningsImport/pos/posExtractor.js
import { db } from "../../../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getAssignmentForDate } from "../../../utils/posAssignmentHistory";

/* ----------------------------------------------------
   HELPERS
---------------------------------------------------- */
function getWeekNumber(isoDate) {
  if (!isoDate) return null;
  const day = Number(String(isoDate).split("-")[2] || 0);
  if (day >= 1 && day <= 7) return 1;
  if (day <= 14) return 2;
  if (day <= 21) return 3;
  if (day <= 28) return 4;
  return 5;
}

const normalizeTerminalId = (v) =>
  String(v ?? "").replace(/\.0$/, "").trim();

const normalizeDate = (v) => String(v ?? "").trim(); // already comes "YYYY-MM-DD" from parser

const parseAmount = (v) =>
  Number(String(v ?? "0").replace(/,/g, "")) || 0;

/* ----------------------------------------------------
   PERFORMANCE CACHES
---------------------------------------------------- */

// Cache assignment lookup per terminal+date
const assignmentCache = {}; // key: "terminalId_date" → assignment object

async function getCachedAssignment(terminalId, dateStr) {
  const t = normalizeTerminalId(terminalId);
  const d = normalizeDate(dateStr);
  const key = `${t}_${d}`;

  if (!t || !d) return null;
  if (assignmentCache[key]) return assignmentCache[key];

  const a = await getAssignmentForDate(t, d);
  assignmentCache[key] = a;
  return a;
}

// Cache vehicle documents
const vehicleCache = {}; // key: carId → vehicle document

async function getCachedVehicle(vehicleId) {
  if (!vehicleId) return null;
  if (vehicleCache[vehicleId]) return vehicleCache[vehicleId];

  const snap = await getDoc(doc(db, "vehicles", vehicleId));
  const data = snap.exists() ? snap.data() : null;
  vehicleCache[vehicleId] = data;
  return data;
}

/* ----------------------------------------------------
   MAIN — RETURN INDIVIDUAL DAILY POS TRANSACTIONS
   (returns { rows, unmatched } to give proof if something skipped)
---------------------------------------------------- */
export async function processPOSTransactions(rows, month) {
  if (!month) return { rows: [], unmatched: [] };

  const [selectedYear, selectedMonth] = String(month).split("-");
  const finalRows = [];
  const unmatched = []; // proof list

  for (const row of rows || []) {
    const dateStr = normalizeDate(row?.date);
    const terminalId = normalizeTerminalId(row?.terminalId);
    const amount = parseAmount(row?.amount);

    if (!dateStr || !terminalId) {
      unmatched.push({
        terminalId: terminalId || String(row?.terminalId ?? ""),
        date: dateStr || String(row?.date ?? ""),
        amount,
        reason: "Missing date or terminalId",
      });
      continue;
    }

    // Only include selected month rows
    if (!dateStr.startsWith(`${selectedYear}-${selectedMonth}`)) continue;

    if (Number.isNaN(amount)) {
      unmatched.push({ terminalId, date: dateStr, amount: row?.amount, reason: "Invalid amount" });
      continue;
    }

    const week = getWeekNumber(dateStr);
    if (!week) {
      unmatched.push({ terminalId, date: dateStr, amount, reason: "Invalid week/day" });
      continue;
    }

    // 1) assignment lookup
    const assignment = await getCachedAssignment(terminalId, dateStr);
    if (!assignment?.vehicleId) {
      unmatched.push({ terminalId, date: dateStr, amount, reason: "No assignment match" });
      continue;
    }

    const carId = assignment.vehicleId;

    // 2) vehicle lookup
    const car = await getCachedVehicle(carId);

    const carNumber = car?.vehicleNumber || "UNKNOWN";
    const company = car?.company || "";
    const vehicleType = car?.vehicleType || "";

    finalRows.push({
      ...row,
      date: dateStr,
      terminalId,
      amount,
      week,
      carId,
      carNumber,
      company,
      vehicleType,
    });
  }

  return { rows: finalRows, unmatched };
}
